import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CommunityAggregateDataType,
  CommunityDataType,
  DiscoverCommunitiesState,
} from "../types/discoverCommunitiesTypes.tsx";
import {
  fetchPersonaCommunitiesAggregateData,
  fetchPersonaCommunitiesData,
} from "../thunks/discoverCommunitiesThunk.tsx";
import { PersonaAPIResponseData } from "../types/personasTypes.tsx";

const initialState: DiscoverCommunitiesState = {
  selectedPersona: null,
  communities: null,
  communityAggregate: null,
  isLoadingCommunityAggregate: false,
  isLoadingCommunities: false,
  aggregates: {},
  loadingStates: {},
  error: null,
};

export const discoverCommunitiesSlice = createSlice({
  name: "discoverCommunities",
  initialState,
  reducers: {
    setSelectedPersona: (
      state,
      action: PayloadAction<PersonaAPIResponseData | null>,
    ) => {
      state.selectedPersona = action.payload;
    },
    clearMatchedCreators: (state) => {
      state.communityAggregate = null;
      state.aggregates = {};
      state.loadingStates = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPersonaCommunitiesData.pending, (state) => {
        state.error = null;
        state.communities = null;
        state.isLoadingCommunities = true;
      })
      .addCase(
        fetchPersonaCommunitiesData.fulfilled,
        (state, action: PayloadAction<CommunityDataType>) => {
          state.communities = action.payload;
          state.isLoadingCommunities = false;
        },
      )
      .addCase(fetchPersonaCommunitiesData.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message;
        state.isLoadingCommunities = false;
      })

      .addCase(
        fetchPersonaCommunitiesAggregateData.pending,
        (state, action) => {
          const personaId = action.meta.arg.id;
          state.isLoadingCommunityAggregate = true;
          state.loadingStates[personaId] = true;
          state.error = null;
        },
      )
      .addCase(
        fetchPersonaCommunitiesAggregateData.fulfilled,
        (
          state,
          action: PayloadAction<{
            personaId: string | number;
            data: CommunityAggregateDataType;
          }>,
        ) => {
          const { personaId, data } = action.payload;
          state.communityAggregate = data;
          state.aggregates[personaId] = data;
          state.isLoadingCommunityAggregate = false;
          state.loadingStates[personaId] = false;
        },
      )
      .addCase(
        fetchPersonaCommunitiesAggregateData.rejected,
        (state, action) => {
          const personaId = action.meta.arg.id;
          state.isLoadingCommunityAggregate = false;
          state.loadingStates[personaId] = false;
          state.error =
            action.payload?.error ||
            action.error.message ||
            "Failed to fetch aggregate";
        },
      );
  },
});

export const { setSelectedPersona, clearMatchedCreators } =
  discoverCommunitiesSlice.actions;

export default discoverCommunitiesSlice.reducer;
